import React, { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import ProductForm from "./ProductForm";
import { productToFormValues } from "./product-form-values";
import { useProductTypeLookup, useMaterialTypeLookup, useProcessLookup } from "@/hooks/useFKLookup";
import { Skeleton } from "@/components/ui/skeleton";
import type { ProductFormData } from "../../../shared/schemas/product.schema";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Package } from "lucide-react";
import { ModalBreadcrumbHeader } from "@/components/shared/ModalBreadcrumbHeader";

export interface ProductFormModalProps {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: (newItem: {
    id: string;
    label: string;
    subLabel?: string;
    original?: any;
  }) => void;
  onCancel?: () => void;
  initialData?: Partial<ProductFormData> & {
    name?: string;
    productName?: string;
    customerId?: string;
    customerName?: string;
    typeId?: string;
    [key: string]: any;
  };
  lockedFields?: string[];
  params?: {
    customerId?: string;
    customerName?: string;
    lockedFields?: string[];
    [key: string]: any;
  };
  breadcrumb?: string[];
  /** Nhân bản: nạp đủ sản phẩm gốc (vật tư, công đoạn, cấu hình công đoạn); initialData chỉ ghi đè mã / tên bản sao */
  cloneFromId?: string;
}

function cleanBreadcrumbs(crumbs?: string[], fallback = "Sản phẩm"): string[] {
  if (!crumbs || crumbs.length === 0) return [fallback];
  const mapped = crumbs.map((c) => {
    if (c === "Thêm mới sản phẩm" || c === "Thêm sản phẩm mới") return "Sản phẩm";
    if (c === "Thêm mới khách hàng" || c === "Thêm khách hàng mới") return "Khách hàng";
    if (c === "Thêm mới vật tư" || c === "Thêm vật tư mới") return "Vật tư";
    if (c === "Tạo nhanh Loại sản phẩm" || c === "Thêm loại sản phẩm") return "Loại sản phẩm";
    if (c === "Tạo nhanh Loại vật tư" || c === "Thêm loại vật tư") return "Loại vật tư";
    return c;
  });
  return mapped.filter((c, i) => i === 0 || c !== mapped[i - 1]);
}

export default function ProductFormModal({
  open,
  onClose,
  onSuccess,
  onCancel,
  initialData,
  lockedFields,
  params,
  breadcrumb,
  cloneFromId,
}: ProductFormModalProps) {
  const queryClient = useQueryClient();
  const { options: productTypeOptions } = useProductTypeLookup();
  const { options: materialTypeOptions } = useMaterialTypeLookup();
  const { options: processOptions } = useProcessLookup();

  // Nhân bản nhanh trước đây đưa nguyên dòng danh sách vào form: đơn giá dạng chữ ("3500.00") nên Tạo báo
  // "Đơn giá phải là số", và thiếu vật tư cấu thành / quy trình sản xuất (FB-O4H7OD, FB-KTAU43)
  const { data: cloneSource, isLoading: isCloneLoading } = useQuery({
    queryKey: ["product", cloneFromId],
    queryFn: () => trpcClient.products.getById.query({ id: cloneFromId! }),
    enabled: !!cloneFromId,
  });
  const cloneDefaults = useMemo(() => {
    if (!cloneFromId || !cloneSource) return null;
    return {
      ...productToFormValues(cloneSource, { productTypeOptions, materialTypeOptions, processOptions }),
      ...(initialData || {}),
      id: undefined,
      currentVersion: undefined,
    };
    // Chỉ dựng một lần khi sản phẩm gốc về, tránh form nạp lại giữa chừng
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneFromId, cloneSource]);

  const handleClose = () => {
    if (onCancel) onCancel();
    if (onClose) onClose();
  };

  const effectiveCustomerId = initialData?.customerId || params?.customerId;
  const effectiveLockedFields =
    lockedFields ||
    params?.lockedFields ||
    (effectiveCustomerId ? ["customerId"] : []);

  const handleSubmit = async (
    data: ProductFormData & { processIds?: string[] },
  ) => {
    try {
      const result = await trpcClient.products.create.mutate(data as any);

      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["combobox"] });
      await queryClient.invalidateQueries({ queryKey: ["product-lookup"] });
      await queryClient.invalidateQueries({
        queryKey: ["entity-select", "products"],
      });

      const label =
        result.productName ||
        result.name ||
        result.productCode ||
        result.code ||
        "";
      toast.success(`Đã thêm thành phẩm: ${label}`);

      if (onSuccess) {
        onSuccess({
          id: result.id,
          label,
          subLabel: result.productCode || result.code || undefined,
          original: {
            ...result,
            name: result.productName,
            code: result.productCode,
          },
        });
      }
      if (onClose) onClose();
      return true;
    } catch (err: any) {
      toast.error(err?.message || "Tạo thành phẩm thất bại");
      return false;
    }
  };

  const effectiveBreadcrumbs = cleanBreadcrumbs(breadcrumb, "Sản phẩm");

  const content = (
    <div className="w-full max-h-[82vh] overflow-y-auto px-1 py-1 space-y-3">
      <ModalBreadcrumbHeader
        breadcrumbs={effectiveBreadcrumbs}
        title="Thêm mới sản phẩm bao bì"
        description="Khai báo quy cách, cấu trúc hộp, định mức BOM và công đoạn sản xuất."
        icon={Package}
        iconColor="text-indigo-600"
      />
      {cloneFromId && !cloneDefaults ? (
        <div className="space-y-4 py-6">
          {isCloneLoading ? (
            <>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </>
          ) : (
            <div className="text-center text-sm text-slate-500">Không tải được sản phẩm gốc để nhân bản</div>
          )}
        </div>
      ) : (
      <ProductForm
        defaultValues={cloneDefaults ?? {
          productName: initialData?.productName || initialData?.name || "",
          customerId: effectiveCustomerId || "",
          customerName: initialData?.customerName || params?.customerName || "",
          typeId: initialData?.typeId || "",
          ...(initialData || {}),
        }}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        submitLabel="Tạo & Chọn ngay"
        enableBlocker={false}
        lockedFields={effectiveLockedFields}
        breadcrumb={effectiveBreadcrumbs}
        isModal
      />
      )}
    </div>
  );

  // When used as standalone modal with explicit open/onClose
  if (open !== undefined && onClose !== undefined) {
    return (
      <Dialog open={open} onOpenChange={(val) => !val && handleClose()}>
        <DialogContent 
          className="w-[96vw] sm:max-w-4xl md:max-w-5xl lg:max-w-6xl xl:max-w-7xl max-h-[95vh] flex flex-col p-4 sm:p-6"
          // Bấm ra ngoài / Esc không đóng form, chỉ đóng bằng Hủy hoặc nút X (FB-9CCX55)
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
            }
          }}
        >
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  // When embedded in ComboboxFK which supplies its own Dialog
  return content;
}
